import React from "react";
import { X } from "lucide-react";
import { PrincipalFormData, PrincipalFormErrors } from "../types";
import { useLobRegion } from "../../../../hooks/useLobRegion";

interface BasicInformationFormProps {
  formData: PrincipalFormData;
  formErrors: PrincipalFormErrors;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onLogoChange: (file: File | null) => void;
  onRemoveLogo: () => void;
  logoUrl?: string | null; // For displaying existing logo URL in edit mode
}

// Helper function to get preview URL for logo
const getLogoPreview = (logo: File): string => {
  return URL.createObjectURL(logo);
};

const BasicInformationForm: React.FC<BasicInformationFormProps> = ({
  formData,
  formErrors,
  onChange,
  onLogoChange,
  onRemoveLogo,
  logoUrl,
}) => {
  const region = useLobRegion();
  return (
    <div className="bg-white rounded-xl p-6 flex flex-col justify-start min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder">
      <h2 className="text-lg font-semibold mb-4">
        Basic Information
      </h2>
      <div className="space-y-6">
        {/* Principal Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Principal Name <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            name="name"
            value={formData.name}
            onChange={onChange}
            placeholder="Enter Principal Name"
            className={`block w-full px-3 py-2 border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
          />
          {formErrors.name && (
            <div className="text-red-500 text-sm mt-1">
              {formErrors.name}
            </div>
          )}
        </div>

        {/* Principal Code and Short Name Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="principalCode" className="block text-sm font-medium text-gray-700 mb-2">
              Principal Code <span className="text-red-500">*</span>
            </label>
            <input
              id="principalCode"
              type="text"
              name="principalCode"
              value={formData.principalCode}
              onChange={onChange}
              placeholder="Enter Principal Code"
              className={`block w-full px-3 py-2 border ${formErrors.principalCode ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
            />
            {formErrors.principalCode && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.principalCode}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="shortName" className="block text-sm font-medium text-gray-700 mb-2">
              Short Name <span className="text-red-500">*</span>
            </label>
            <input
              id="shortName"
              type="text"
              name="shortName"
              value={formData.shortName}
              onChange={onChange}
              placeholder="Enter Short Name"
              className={`block w-full px-3 py-2 border ${formErrors.shortName ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
            />
            {formErrors.shortName && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.shortName}
              </div>
            )}
          </div>
        </div>

        {/* Logo Upload and Merchant ID Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="logoUrl" className="block text-sm font-medium text-gray-700 mb-2">
              Logo
            </label>
            {/* Logo Preview */}
            <div className="relative w-full h-32 mb-2 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-center overflow-hidden">
              {formData.logoUrl ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      onRemoveLogo();
                      const fileInput = document.getElementById("logoFile") as HTMLInputElement;
                      if (fileInput) fileInput.value = "";
                    }}
                    className="absolute top-2 right-2 p-1 bg-white rounded-full hover:bg-gray-100 z-10"
                  >
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                  <img src={getLogoPreview(formData.logoUrl)} alt="Logo preview" className="w-full h-full object-contain" />
                </>
              ) : logoUrl ? (
                <img src={logoUrl} alt="Current logo" className="w-full h-full object-contain" />
              ) : (
                <span className="text-gray-400">No image</span>
              )}
            </div>
            <input
              type="file"
              id="logoFile"
              accept="image/*"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0];
                onLogoChange(file || null);
              }}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
            />
            <p className="text-sm text-gray-500 mt-1">
              Optional: Upload logo image (Max 5MB)
            </p>
            {formErrors.logoUrl && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.logoUrl}
              </div>
            )}
          </div>

          {region.indiaFields && (
          <div>
            <label htmlFor="merchantId" className="block text-sm font-medium text-gray-700 mb-2">
              Merchant ID
            </label>
            <input
              id="merchantId"
              type="text"
              name="merchantId"
              value={formData.merchantId}
              onChange={onChange}
              placeholder="Enter Merchant ID"
              className={`block w-full px-3 py-2 border ${formErrors.merchantId ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand`}
            />
            {formErrors.merchantId && (
              <div className="text-red-500 text-sm mt-1">
                {formErrors.merchantId}
              </div>
            )}
            <p className="text-sm text-gray-500 mt-1">
              Optional: Merchant identifier
            </p>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BasicInformationForm;
